import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import {CodeViewer} from "./CodeViewer";

// @Todo lazyload
// Dummy blueprint data, you might want to fetch these from an API or other source.

const frontendMathJSON = `
{
  "dependencies": {
    "react": "^18.2.0 (fixed)",
    "react-dom": "^18.2.0 (fixed)",   
    "react-katex": "^3.0.1"
  }
}`;

const frontendBasePackageJSON = `
{
  "dependencies": {
    "react": "^18.2.0 (fixed)",
    "react-dom": "^18.2.0 (fixed)"
  }
}`;

const frontendMUIBasePackageJSON = `
{
  "dependencies": {
    "react": "^18.2.0 (fixed)",
    "react-dom": "^18.2.0 (fixed)",
    "@mui/material": "^5.16.1 (fixed)",
    "@mui/icons-material": "^5.16.1 (fixed)",
    "@emotion/react": "^11.10.6 (fixed)",
    "@emotion/styled": "^11.10.6 (fixed)"
  }
}`;

const frontendMathCode = `
import React from "react";
import { InlineMath, BlockMath } from 'react-katex';
import 'katex/dist/katex.min.css';


const LatexContent = (props) => {
    const { content } = props;
    let clearContent = content.replace('<BlockMath>\\n', '<BlockMath>').replace('\\n</BlockMath>', '</BlockMath>');

    const latexPattern = /<BlockMath>(.*?)<\\/BlockMath>|<InlineMath>(.*?)<\\/InlineMath>/g;
    const renderLatex = (text) => {
        const lines = text.split('\\n');
        return lines.map((line, lineIndex) => (
            <React.Fragment key={'line-' + lineIndex}>
                {line.split(latexPattern).map((part, partIndex) => {
                    if (partIndex % 3 === 1 && part) {
                        return <BlockMath errorColor={'#cc0000'} key={'block-' + lineIndex + '-' + partIndex}>{part}</BlockMath>;
                    } else if (partIndex % 3 === 2 && part) {
                        return <InlineMath errorColor={'#cc0000'} key={'inline-' + lineIndex + '-' + partIndex}>{part}</InlineMath>;
                    }
                    return part;
                })}
                {lineIndex < lines.length - 1 && <br />}
            </React.Fragment>
        ));
    };
    return <div>{renderLatex(clearContent)}</div>;
};


const onRenderChatMessage = async (params) => {
    const {content, role} = params.messages[params.msgIndex];

    if (content.includes('BlockMath') || content.includes('InlineMath')) {
        return <LatexContent content={content} />
    }
};

export default {
    onRenderChatMessage
}

window.onRenderChatMessage = onRenderChatMessage;
`;

const frontendRenderSettings = `
import React, { useEffect } from "react";

const Header = ({ setRenderSettings}) => {
    useEffect(() => {
        setRenderSettings({
            disableEmojiButton: true,
            disableShareButton: true,
            disableTextToSpeechButton: true,
            disableCopyButton: true,
            disableChatAvatar: true,
            disableSentLabel: true,
            disableContextItems: true,
            disableMobileLeftButton: true,
            disableBalanceView: true,
            disableChatModelsSelect: true,        
        });
    }, [setRenderSettings]);

}

const exports = { Header };
window.contentRender = exports;
export default exports;
`;

const emptyPackageJSON = `
{
  "dependencies": {
  }
}`;

const bigJSJSON = `
{
  "dependencies": {
      "big.js": "^6.0.0"
  }
}`;

const executeNodeJSCode = (e) => `
import vm from 'vm';

export const handler = async (event) => {
    ${e === 'onRequest' ? `const meta = {};` : `// REQUEST_CHAT_MODEL flag auto-loops command responses back to the chat model for continuous interaction
    const meta = {_meta_actions: ["REQUEST_CHAT_MODEL"]}`}
    
    const executeCode = async (sourceCode) => {
        // Create a new script from the source code
        const script = new vm.Script(sourceCode);

        // Create a new context for the script to run in
        const context = {
            require: (id) => {
                // allow to "require" any available module
                return rootContext.require(id)
            },
            ...rootContext,
            console,
            module: { exports: {} }
        }
        vm.createContext(context);

        // Run the script in the context
        script.runInContext(context);

        // Extract the handler function from the context
        const { handler } = context.module.exports;

        // Execute the handler function and return the result
        const data = await handler();
        return { data, ...meta };
    };


    const actions = [
        [/\`\`javascript\\s*([\\s\\S]*?)\\s*\`\`/, async (match) => await executeCode(match[1])],
    ];

    for (let [regex, action] of actions) {
        const lastMessage = event.payload.messages[event.payload.messages.length - 1].content;
        
        const match = lastMessage?.match(regex);
        
        if (match) {
            return await action(match);
        }              
    }
    
    return { type: 'CONTINUE' }
};
`

const googleSearchCode = (e) => `
export const handler = async (event) => {
    ${e === 'onRequest' ? `const meta = {};` : `// REQUEST_CHAT_MODEL flag auto-loops command responses back to the chat model for continuous interaction
    const meta = {_meta_actions: ["REQUEST_CHAT_MODEL"]}`}   
    
    const googleSearch = async (match) => {
        const q = match[1];
        let data;
        try {
            const response = await axios.get('https://www.googleapis.com/customsearch/v1', {
                params: {
                    key: '{{secrets.googlesearch_api_key}}',
                    cx: '{{secrets.googlesearch_engine_id}}',
                    q
                }
            });

            data = response?.data?.items?.map(({ title, link, snippet }) => ({ title, link, snippet }));

        } catch (error) {
            data = {error: error.message}
        }

        return { data, ...meta }
    }


    const actions = [
        [/search\\("(.*)"\\)/, async(match) => await googleSearch(match)],
    ];

    for (let [regex, action] of actions) {
        const lastMessage = event.payload.messages[event.payload.messages.length - 1].content;
        
        const match = lastMessage?.match(regex);
        
        if (match) {
            return await action(match);
        }              
    }
    
    return { type: 'CONTINUE' }
};`;

const emptyFunction = (e) => `
export const handler = async (event) => {
    
};`;


const BLUEPRINTS = {
    /**
     * F R O N T E N D
     */

    'Frontend/contentRender.json': [
        { title: 'Default package.json', description: 'base npm dependencies', code: frontendBasePackageJSON },
        { title: 'react, mui', description: 'provides a simple library of React components', code: frontendMUIBasePackageJSON },
        { title: 'react-katex', description: 'renders math expressions', code: frontendMathJSON}],

    'Frontend/contentRender.js': [
        { title: 'Render Settings', description: 'turn on/off some frontend settings', code: frontendRenderSettings }
        // { title: 'Hello, world', description: 'example', code: frontendHelloWorldCode },
        // { title: 'Math expressions render', description: 'using react-katex', code: frontendMathCode, dependencies: frontendMathJSON }
    ],

    /**
     * E V E N T S
     */
    'Events/onRequest.js': [
        { title: 'Empty function', description: 'no code', code: emptyFunction() },
        { title: 'NodeJS Interpreter', description: ' automatically executes code on user input', code: executeNodeJSCode('onRequest') },
        { title: 'Google search', description: ' enable google search("") command on user input', code: googleSearchCode('onRequest') }
    ],
    'Events/onResponse.js': [
        { title: 'Empty function', description: 'no code', code: emptyFunction() },
        { title: 'NodeJS Interpreter', description: ' automatically executes code on LLM output', code: executeNodeJSCode('onResponse') },
        { title: 'Google search', description: ' enable google search("") command on LLM output', code: googleSearchCode('onResponse') }
    ],
    'Events/onAddMessages.js': [{ title: 'Empty function', description: 'no code', code: emptyFunction() }],

    'Events/onResponse.json': [
        { title: 'Empty package.json', description: 'no npm dependencies', code: emptyPackageJSON },
        { title: 'Install Big.js 6', description: 'a small, fast, library for decimal arithmetic', code: bigJSJSON }
    ],

    'Events/onRequest.json': [{ title: 'Empty package.json', description: 'no npm dependencies', code: emptyPackageJSON }],
    'Events/onAddMessages.json': [{ title: 'Empty package.json', description: 'no npm dependencies', code: emptyPackageJSON }],

    // 'API/endpoints.js': [
    //     { title: 'Blueprint 1', description: 'Short description for blueprint 1', code: 'console.log("Blueprint 1 code");' }
    // ],

};

export function BlueprintDialog({ open, onClose, onCreate, file, isFullscreen, setFullscreen }) {
    const [selectedBlueprint, setSelectedBlueprint] = useState(null);
    const [code, setCode] = useState('');

    useEffect(() => {
        if (open && BLUEPRINTS[file] && BLUEPRINTS[file].length > 0) {
            const firstBlueprint = BLUEPRINTS[file][0];
            setSelectedBlueprint(firstBlueprint);
            setCode(firstBlueprint.code);
        }

    }, [open, file, isFullscreen]);

    if (isFullscreen) {
        setFullscreen(false);
    }

    const handleSelectChange = (event) => {
        const blueprint = BLUEPRINTS[file].find(b => b.title === event.target.value);
        setSelectedBlueprint(blueprint);
        setCode(blueprint ? blueprint.code : '');
    };

    const handleCreate = () => {
        onCreate(code, selectedBlueprint.dependencies);
    };

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>Choose Blueprint</DialogTitle>
            <DialogContent>
                <FormControl fullWidth style={{ marginTop: 20 }}>
                    <InputLabel htmlFor="blueprint-select">Choose Blueprint</InputLabel>
                    <Select
                        value={selectedBlueprint ? selectedBlueprint.title : ''}
                        onChange={handleSelectChange}
                        label="Choose Blueprint"
                        id="blueprint-select"
                        fullWidth
                    >
                        {BLUEPRINTS[file]?.map(blueprint => (
                            <MenuItem key={blueprint.title} value={blueprint.title}>
                                {blueprint.title} - {blueprint.description}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <CodeViewer
                    source={code} noCopy={true}
                    limitedWidth={true}
                    language={"javascript"}
                    className="codeWrapperAPI"
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary">
                    Cancel
                </Button>
                <Button variant="contained" color="primary" onClick={handleCreate} disabled={!selectedBlueprint}>
                    Create
                </Button>
            </DialogActions>
        </Dialog>
    );
}
